import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import { useTranslation } from 'react-i18next';
import IEventBaseDataProps from './IEventBaseDataProps';
import { IGroup } from '../../../models/IEvent';
import IEditableRider from '../../../models/IEditableRider';
import EditionTableToolbar from '../../common/EditionTableToolbar';
import ConfirmDialog from '../../common/ConfirmDialog';
import { format } from 'util';
import RidersDistributionDialog from './RidersDistributionDialog';
import HelpTooltip from '../../common/HelpTooltip';



const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 280,
        },
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
    }));

const showFromLgTableCellSx = { display: { xs: 'none', lg: 'table-cell' } } as const;

interface IEditGroupDialogProps {
    group: IGroup;
    existingGroups: IGroup[];
    // open: boolean;
    onCompleteEdit: (group: IGroup) => void;
    onCancelEdit: () => void;
}
function EditGroupDialog(props: IEditGroupDialogProps) {
    const { t } = useTranslation("common");
    const validateName = (name: string): string | undefined => {
        if (!name.trim()) {
            return format(t("commonErrors.requiredField"), t("groups.name")) as string;
        }
        const isDuplicate = props.existingGroups.some(
            g => g.groupId !== props.group.groupId && g.name.toLowerCase() === name.toLowerCase()
        );
        return isDuplicate ? t("groups.duplicateName") as string : undefined;
    };
    const [currentGroup, setCurrentGroup] = React.useState<IGroup>(props.group);
    const [nameError, setNameError] = React.useState<string | undefined>(() => validateName(props.group.name));
    // const [open, setOpen] = useState(props.open);
    const handleClose = () => {
        props.onCancelEdit();
    }
    const handleCompleteEdit = () => {
        props.onCompleteEdit(currentGroup);
    }
    return (
        <Dialog open={true} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{t("groups.group")}</DialogTitle>
            <DialogContent>

                <TextField
                    autoFocus
                    margin="dense"
                    id="groupname"
                    label={t("groups.name")}
                    type="text"
                    fullWidth
                    value={currentGroup.name}
                    error={Boolean(nameError)}
                    helperText={nameError}
                    onChange={e => {
                        const newGroup: IGroup = { ...currentGroup, name: e.target.value };
                        setCurrentGroup(newGroup);
                        setNameError(validateName(e.target.value));
                    }}
                />
                <TextField
                    id="groupTargetTime"
                    label={<>{t("groups.targetTime")}<HelpTooltip text={t("tooltips.groupTargetTime")} /></>}
                    type="number"
                    fullWidth
                    defaultValue="300"
                    value={currentGroup.targetTime}
                    onChange={e => {
                        const newGroup: IGroup = { ...currentGroup, targetTime: parseInt(e.target.value) };
                        setCurrentGroup(newGroup);
                    }}
                />
                <TextField
                    margin="dense"
                    id="groupFirstTag"
                    label={<>{t("groups.firstTag")}<HelpTooltip text={t("tooltips.groupFirstTag")} /></>}
                    type="number"
                    value={currentGroup.startingTag}
                    onChange={e => {
                        const newGroup: IGroup = { ...currentGroup, startingTag: parseInt(e.target.value) };
                        setCurrentGroup(newGroup);
                    }}
                />
                <TextField
                    margin="dense"
                    id="groupLastTag"
                    label={<>{t("groups.lastTag")}<HelpTooltip text={t("tooltips.groupLastTag")} /></>}
                    type="number"
                    value={currentGroup.endingTag}
                    onChange={e => {
                        const newGroup: IGroup = { ...currentGroup, endingTag: parseInt(e.target.value) };
                        setCurrentGroup(newGroup);
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCompleteEdit} color="primary" disabled={Boolean(nameError)}>
                    {t("commonActions.ok")}
                </Button>
                <Button onClick={handleClose} color="primary">
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>);
}

interface IEventGroupsData {
    groups: IGroup[]
}

interface IEventGroupsProps extends IEventBaseDataProps<IEventGroupsData> {
    onMoveRiders?: (fromGroupId: string, toGroupId: string) => void;
    raceId?: string;
    riders?: IEditableRider[];
    onDistributeRiders?: (updatedRiders: IEditableRider[], updatedGroups: IGroup[]) => void;
}

interface IMoveRidersDialogProps {
    sourceGroup: IGroup;
    otherGroups: IGroup[];
    onConfirm: (targetGroupId: string) => void;
    onCancel: () => void;
}

function MoveRidersDialog(props: IMoveRidersDialogProps) {
    const { t } = useTranslation("common");
    const [targetGroupId, setTargetGroupId] = React.useState<string>(
        props.otherGroups.length > 0 ? props.otherGroups[0].groupId : ''
    );

    const handleConfirm = () => {
        if (targetGroupId) {
            props.onConfirm(targetGroupId);
        }
    };

    return (
        <Dialog open={true} aria-labelledby="move-riders-dialog-title">
            <DialogTitle id="move-riders-dialog-title">
                {format(t("groups.moveRidersTitle"), props.sourceGroup.name)}
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="dense">
                    <InputLabel id="target-group-label">{t("groups.targetGroup")}</InputLabel>
                    <Select
                        labelId="target-group-label"
                        value={targetGroupId}
                        label={t("groups.targetGroup")}
                        onChange={e => setTargetGroupId(e.target.value as string)}
                    >
                        {props.otherGroups.map(g => (
                            <MenuItem key={g.groupId} value={g.groupId}>{g.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleConfirm} color="primary" disabled={!targetGroupId}>
                    {t("commonActions.ok")}
                </Button>
                <Button onClick={props.onCancel} color="primary">
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function EventGroups(props: IEventGroupsProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const [selected, setSelected] = React.useState<string[]>([]);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [editingGroup, setEditingGroup] = React.useState<IGroup | undefined>(undefined);
    const [groups, setGroups]= React.useState(props.data.groups);
    const [movingFromGroup, setMovingFromGroup] = React.useState<IGroup | undefined>(undefined);
    const [moveConfirmation, setMoveConfirmation] = React.useState<{ from: IGroup; to: IGroup } | undefined>(undefined);
    const [openDistributionDialog, setOpenDistributionDialog] = React.useState(false);
    const rowCount = props.data.groups?.length ?? 0;

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };
    const handleDoubleClick = (event: React.MouseEvent<unknown>, groupId: string) => {
        editGroup(groupId);
    }

    const handleEdit = (event: React.MouseEvent<unknown>) => {
        if (selected.length === 1) {
            editGroup(selected[0]);
        }
    }

    const editGroup = (groupId: string) => {
        const group = props.data.groups.find(g => g.groupId === groupId);
        if (group) {
            const group2: IGroup = { ...group, targetTime: group.targetTime / 10000000 };
            setEditingGroup(group2);
        } else {
            console.log('no group');
        }
    }

    const handleAdd = () => {
        const groupId = Math.max(...props.data.groups.map(g => parseInt(g.groupId))) + 1;
        const newGroup: IGroup = {
            groupId: groupId.toString(),
            name: '',
            targetTime: 300
        };
        setEditingGroup(newGroup);
    }

    const handleCompleteEdit = (group: IGroup) => {
        group.targetTime = group.targetTime * 10000000;
        const i = props.data.groups.findIndex(g => g.groupId === group.groupId);
        let resultingGroups: IGroup[];
        if (i === -1) {
            // new group
            resultingGroups = props.data.groups;
            resultingGroups.push(group);
        } else {
            resultingGroups = props.data.groups;
            resultingGroups[i] = group;
        }
        if (props.onDataChange) {
            props.onDataChange({
                groups: resultingGroups
            });
        }
        setEditingGroup(undefined);
    }


    const handleDelete = (event: React.MouseEvent<unknown>) => {
        setOpenConfirmation(true);
    }

    const deleteSelectedGroups = () => {
        setOpenConfirmation(false);
        const resultingGroups = props.data.groups.filter(g => selected.indexOf(g.groupId) === -1);
        setSelected([]);
        setGroups(resultingGroups);
        if (props.onDataChange) {
            props.onDataChange({
                groups: resultingGroups
            });
        }
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = groups.map((n) => n.groupId);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };
    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    const handleCloseConfirmation = () => {
        setOpenConfirmation(false);
    };
    const filterGroups = (event: any) => {
        const filterValue: string = event.target.value.toLowerCase();
        if (filterValue.length > 0) {
            const filteredGroups = props.data.groups.filter(g =>
                g.name.toLowerCase().indexOf(filterValue) >= 0);
            setGroups(filteredGroups);
        } else {
            setGroups(props.data.groups);
        }

    }

    const handleMoveRidersClick = (event: React.MouseEvent<unknown>, groupId: string) => {
        event.stopPropagation();
        const group = props.data.groups.find(g => g.groupId === groupId);
        if (group) {
            setMovingFromGroup(group);
        }
    };

    const handleMoveRidersConfirmTarget = (targetGroupId: string) => {
        if (movingFromGroup) {
            const targetGroup = props.data.groups.find(g => g.groupId === targetGroupId);
            if (targetGroup) {
                setMoveConfirmation({ from: movingFromGroup, to: targetGroup });
            }
        }
        setMovingFromGroup(undefined);
    };

    const handleMoveRidersExecute = () => {
        if (moveConfirmation && props.onMoveRiders) {
            props.onMoveRiders(moveConfirmation.from.groupId, moveConfirmation.to.groupId);
        }
        setMoveConfirmation(undefined);
    };

    return (
        <React.Fragment>
            <ConfirmDialog
                open={openConfirmation}
                onAccept={deleteSelectedGroups}
                onCancel={handleCloseConfirmation}
                title={t("groups.deleteGroups")}
                message={format(t("groups.confirmDelete"), selected.length.toString())} />

            {moveConfirmation && (
                <ConfirmDialog
                    open={true}
                    onAccept={handleMoveRidersExecute}
                    onCancel={() => setMoveConfirmation(undefined)}
                    title={t("groups.moveRiders")}
                    message={format(t("groups.confirmMoveRiders"), moveConfirmation.from.name, moveConfirmation.to.name)}
                />
            )}

            {movingFromGroup && (
                <MoveRidersDialog
                    sourceGroup={movingFromGroup}
                    otherGroups={props.data.groups.filter(g => g.groupId !== movingFromGroup.groupId)}
                    onConfirm={handleMoveRidersConfirmTarget}
                    onCancel={() => setMovingFromGroup(undefined)}
                />
            )}

            {editingGroup === undefined ? (<></>) : (<EditGroupDialog
                group={editingGroup}
                existingGroups={props.data.groups}
                onCompleteEdit={handleCompleteEdit}
                onCancelEdit={() => setEditingGroup(undefined)} />)}

            {openDistributionDialog && props.raceId && props.riders && props.onDistributeRiders && (
                <RidersDistributionDialog
                    raceId={props.raceId}
                    existingGroups={props.data.groups}
                    existingRiders={props.riders}
                    onApply={(updatedRiders, updatedGroups) => {
                        setOpenDistributionDialog(false);
                        props.onDistributeRiders!(updatedRiders, updatedGroups);
                    }}
                    onCancel={() => setOpenDistributionDialog(false)}
                />
            )}

            <Paper className={classes.paper}>

                <EditionTableToolbar
                    numSelected={selected.length}
                    headerLabel={t("eventsetup.groups")}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAdd={handleAdd}
                    onFilterChanged={filterGroups}
                />
                {props.raceId && props.riders && props.onDistributeRiders && (
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CallSplitIcon />}
                            onClick={() => setOpenDistributionDialog(true)}
                        >
                            {t("distribution.buttonLabel")}
                        </Button>
                    </Box>
                )}
                <TableContainer component={Paper}>
                    <Table className={classes.table} aria-label="groups">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < rowCount}
                                        checked={rowCount > 0 && selected.length === rowCount}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                                <TableCell align="left" >{t("groups.name")}</TableCell>
                                <TableCell align="right">{t("groups.targetTime")}</TableCell>
                                <TableCell align="right" sx={showFromLgTableCellSx}>{t("groups.firstTag")}</TableCell>
                                <TableCell align="right" sx={showFromLgTableCellSx}>{t("groups.lastTag")}</TableCell>
                                {props.onMoveRiders && <TableCell align="right" padding="none" />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.map((g, index) => {
                                const isItemSelected = isSelected(g.groupId);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <TableRow key={g.groupId}
                                        hover
                                        onClick={(event) => handleClick(event, g.groupId)}
                                        onDoubleClick={(event) => handleDoubleClick(event, g.groupId)}
                                        role="checkbox"
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isItemSelected}
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </TableCell>
                                        <TableCell component="th" scope="row" id={labelId}>
                                            {g.name}
                                        </TableCell>
                                        <TableCell align="right">{g.targetTime / 10000000}</TableCell>
                                        <TableCell align="right" sx={showFromLgTableCellSx}>{g.startingTag}</TableCell>
                                        <TableCell align="right" sx={showFromLgTableCellSx}>{g.endingTag}</TableCell>
                                        {props.onMoveRiders && (
                                            <TableCell align="right" padding="none">
                                                <Tooltip title={t("groups.moveRiders").toString()}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={t<string>("groups.moveRiders")}
                                                            disabled={props.data.groups.length <= 1}
                                                            onClick={(event) => handleMoveRidersClick(event, g.groupId)}
                                                        >
                                                            <SwapHorizIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </React.Fragment>
    );
}

export default EventGroups;